import { type JSX, useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { formValueSelector, type InjectedFormProps, reduxForm, SubmissionError } from "redux-form";
import { ActionButton, Callout, CalloutIntent, HStack, LoadingSpinner, SymbolIcon, Tooltip, useTooltip } from "widgets";

import { simpleFieldValidation } from "../../app-utils/form.js";
import { pluralizeWord } from "../../app-utils/string.js";
import { type OraclePasswordLimit, type User } from "../../graphql.js";
import { canResetPassword, deleteTokenAndLogout, setPassword } from "../../redux/modules/auth.js";
import { type IAppState } from "../../redux/reducer.js";
import { type ReduxDispatch } from "../../redux/redux-utils.js";
import { PASSWORD_STATUS } from "../../settings/settings.js";
import { WelcomingPage } from "./LoginPage.js";
import { PasswordInputField } from "./redux-form/InputField.js";

export interface IUserAccount extends Omit<Partial<User>, "accountStatus"> {
  accountStatus: PASSWORD_STATUS;
}

export interface CreateAccountPasswordFormFieldsProps {
  passwordRules: OraclePasswordLimit;
}
export type ICreateAccountPasswordPageProps = CreateAccountPasswordFormFieldsProps;

export enum CreatePasswordFormNames {
  FORM_NAME = "setPasswordForm",
  FIELD_PASS = "FIELD_PASS",
  FIELD_PASS_VALIDATE = "FIELD_PASS_VALIDATE",
  FIELD_OLD_PASS = "FIELD_OLD_PASS", // only necessary when modifying your own password
}

export function CreateAccountPasswordFormImpl(props: ICreateAccountPasswordPageProps & InjectedFormProps<any, any>) {
  const { handleSubmit, submitting, error } = props;

  const dispatch = useDispatch<ReduxDispatch>();
  const [passwordResetToken] = useState(new URLSearchParams(location.search).get("token"));
  const [tokenValidationErrorMessage, setTokenValidationErrorMessage] = useState<string | JSX.Element>(null);
  const [validationMessage, setValidationMessage] = useState<string | JSX.Element>(null);
  const [targetUser, setTargetUser] = useState<IUserAccount>(null);
  const [isValidatingToken, setValidatingToken] = useState(true);

  useEffect(() => {
    dispatch(canResetPassword(passwordResetToken))
      .then((user: IUserAccount) => {
        setTargetUser(user);
        if (!user.accountStatus.startsWith(PASSWORD_STATUS.EXPIRED)) {
          setValidationMessage(
            <div>
              Your account status does not need a password change. <br />
              Please <Link to={`/login`}>sign in</Link> or contact your administrator.
            </div>,
          );
        }
        setValidatingToken(false);
      })
      .catch((error: Error) => {
        //if (error.message?.startsWith("401")) {
        setTokenValidationErrorMessage(
          <div>
            Could not verify the provided token: {error.message}. <br />
            Please contact your administrator or try to <Link to={`/login`}>sign in</Link>
          </div>,
        );

        setValidatingToken(false);
      });
  }, []);

  function onSetPassword(values) {
    const payload = {
      token: passwordResetToken,
      userName: targetUser.username,
      password: values[CreatePasswordFormNames.FIELD_PASS],
    };

    return dispatch(setPassword(payload)).then(
      () => {
        /* empty */
      },
      (rejected: Error) => {
        throw new SubmissionError({ _error: rejected.message });
      },
    );
  }

  return (
    <WelcomingPage title={`Welcome ${targetUser?.firstName ?? ""} ${targetUser?.lastName ?? ""}`}>
      {isValidatingToken ? (
        <LoadingSpinner />
      ) : tokenValidationErrorMessage ? (
        <Callout intent={CalloutIntent.Danger}>{tokenValidationErrorMessage}</Callout>
      ) : validationMessage ? (
        validationMessage
      ) : (
        <>
          Please create an account password.
          <CreateAccountPasswordFormFields passwordRules={props.passwordRules} />
        </>
      )}
      {error && <Callout intent={CalloutIntent.Danger}>{error}</Callout>}
      {!validationMessage && !tokenValidationErrorMessage && !isValidatingToken && (
        <ActionButton label={"Save"} isLoading={submitting} onPress={handleSubmit(onSetPassword)} />
      )}
    </WelcomingPage>
  );
}

export function CreateAccountPasswordFormFields(props: CreateAccountPasswordFormFieldsProps) {
  const passwordValue = useSelector<IAppState, string>(
    (state) => formValueSelector(CreatePasswordFormNames.FORM_NAME)(state, CreatePasswordFormNames.FIELD_PASS),
    shallowEqual,
  );
  const {
    minPasswordCharLength,
    maxPasswordBytesLength,
    minDigits,
    minLowerAsciiChars,
    minUpperAsciiChars,
    minOtherChars,
  } = props.passwordRules;

  const [showValidationRules, setShowValidationRules] = useState(false);
  const inputRef = useRef<HTMLInputElement>(undefined);

  useEffect(() => {
    const inputElement = inputRef.current;

    if (inputElement) {
      inputElement.addEventListener("focus", () => setShowValidationRules(true));
      inputElement.addEventListener("blur", () => setShowValidationRules(false));
    }
  }, []);

  const validateExpectedLength = () => hasExpectedLength(passwordValue, minPasswordCharLength, maxPasswordBytesLength);
  const validateExpectedDigitsCharacters = () => hasExpectedDigitsCharacters(passwordValue, minDigits);
  const validateExpectedLowercaseCharacters = () => hasExpectedLowercaseCharacters(passwordValue, minLowerAsciiChars);
  const validateExpectedUppercaseCharacters = () => hasExpectedUppercaseCharacters(passwordValue, minUpperAsciiChars);
  const validateExpectedSpecialCharacters = () => hasExpectedSpecialCharacters(passwordValue, minOtherChars);

  const getValidationIcon = (validator) => {
    const isValid = !(!passwordValue || validator());
    return (
      <SymbolIcon icon={isValid ? "check" : "close"} style={{ fontSize: 14, color: isValid ? "#A9C44F" : "#F7554E" }} />
    );
  };

  function getPasswordRulesValidation() {
    return (
      <>
        <HStack>
          {getValidationIcon(validateExpectedLength)} Between {minPasswordCharLength} and {maxPasswordBytesLength}{" "}
          characters.
        </HStack>
        {minDigits > 0 && (
          <HStack>
            {getValidationIcon(validateExpectedDigitsCharacters)} At least {minDigits} digit(s).
          </HStack>
        )}
        {minLowerAsciiChars > 0 && (
          <div>
            {getValidationIcon(validateExpectedLowercaseCharacters)} At least {minLowerAsciiChars} lowercase letter(s).
          </div>
        )}
        {minUpperAsciiChars > 0 && (
          <HStack>
            {getValidationIcon(validateExpectedUppercaseCharacters)} At least {minUpperAsciiChars} uppercase letters(s).
          </HStack>
        )}
        {minOtherChars > 0 && (
          <HStack>
            {getValidationIcon(validateExpectedSpecialCharacters)} At least {minOtherChars} special character(s).
          </HStack>
        )}
      </>
    );
  }

  // a different "validate" value will force the input field to unregister/register again
  const passConfirmationValidation = useMemo(() => [simpleFieldValidation], []);
  const passRulesValidation = useMemo(() => [simpleFieldValidation], []);

  const tooltipRef = useRef(null);
  const { state, tooltipProps, triggerProps, arrowProps } = useTooltip(inputRef, tooltipRef, {
    placement: "right",
    isOpen: showValidationRules,
    offset: 45, // because the show icon
  });

  return (
    <>
      <PasswordInputField
        ref={inputRef}
        label={"New password"}
        name={CreatePasswordFormNames.FIELD_PASS}
        validate={passRulesValidation}
        autocomplete={"new-password"}
        allowReadability
        {...triggerProps}
      />
      <PasswordInputField
        label={"Confirm new password"}
        name={CreatePasswordFormNames.FIELD_PASS_VALIDATE}
        validate={passConfirmationValidation}
        autocomplete={"new-password"}
      />
      {state.isOpen && (
        <Tooltip domProps={tooltipProps} arrowProps={arrowProps} ref={tooltipRef} title={"Passwords mandatory rules"}>
          {getPasswordRulesValidation()}
        </Tooltip>
      )}
    </>
  );
}

const validateAccountPasswordFields = (values: any, props: CreateAccountPasswordFormFieldsProps) => {
  const passwordValue: string = values[CreatePasswordFormNames.FIELD_PASS];

  if (!passwordValue) {
    return {};
  }

  const {
    minPasswordCharLength,
    maxPasswordBytesLength,
    minDigits,
    minLowerAsciiChars,
    minUpperAsciiChars,
    minOtherChars,
    forbiddenChars,
  } = props.passwordRules;

  const error: string =
    hasExpectedLength(passwordValue, minPasswordCharLength, maxPasswordBytesLength) ||
    hasExpectedCharset(passwordValue) ||
    hasExpectedDigitsCharacters(passwordValue, minDigits) ||
    hasExpectedLowercaseCharacters(passwordValue, minLowerAsciiChars) ||
    hasExpectedUppercaseCharacters(passwordValue, minUpperAsciiChars) ||
    hasExpectedSpecialCharacters(passwordValue, minOtherChars) ||
    checkForbiddenCharacters(passwordValue, forbiddenChars);

  if (error) {
    return { [CreatePasswordFormNames.FIELD_PASS]: error };
  }

  if (values[CreatePasswordFormNames.FIELD_PASS] !== values[CreatePasswordFormNames.FIELD_PASS_VALIDATE]) {
    return { [CreatePasswordFormNames.FIELD_PASS_VALIDATE]: "Passwords must match" };
  }

  return {};
};

const CreateAccountPasswordForm = reduxForm({
  form: CreatePasswordFormNames.FORM_NAME,
  validate: validateAccountPasswordFields,
})(CreateAccountPasswordFormImpl) as any;

const AccountSettingsPasswordPage = (props) => {
  // passwordRules need to be passed through props so that they are injected in the redux form validation static function
  const passwordRules = useSelector<IAppState, OraclePasswordLimit>(
    (state) => state.config.configuration?.oraclePasswordLimits,
    shallowEqual,
  );
  const dispatch = useDispatch<ReduxDispatch>();
  useEffect(() => dispatch(deleteTokenAndLogout()), []); // in case the user is already logged in, using this account or another

  return <CreateAccountPasswordForm passwordRules={passwordRules} {...props} />;
};

export { validateAccountPasswordFields };
export default AccountSettingsPasswordPage;

const hasExpectedLength = (value, minPasswordCharLength, maxPasswordBytesLength) => {
  if (value.length > maxPasswordBytesLength || value.length < minPasswordCharLength) {
    return `Password must have between ${minPasswordCharLength} and ${maxPasswordBytesLength} characters`;
  }
};

const hasExpectedCharset = (value) => {
  for (let i = 0; i < value.length; i++) {
    if (value.charCodeAt(i) > 126 || value.charCodeAt(i) < 33) {
      return `Only printable characters are allowed`;
    }
  }
};

const hasExpectedDigitsCharacters = (value, minDigits) => {
  if ((value.match(/\d/g) || []).length < minDigits) {
    return `Password must have at least ${minDigits} ${pluralizeWord("digit", minDigits)}`;
  }
};

const hasExpectedLowercaseCharacters = (value, minLowerAsciiChars) => {
  if ((value.match(/[a-z]/g) || []).length < minLowerAsciiChars) {
    return `Password must have at least ${minLowerAsciiChars} lowercase ${pluralizeWord("letter", minLowerAsciiChars)}`;
  }
};

const hasExpectedUppercaseCharacters = (value, minUpperAsciiChars) => {
  if ((value.match(/[A-Z]/g) || []).length < minUpperAsciiChars) {
    return `Password must have at least ${minUpperAsciiChars} uppercase ${pluralizeWord("letter", minUpperAsciiChars)}`;
  }
};

const hasExpectedSpecialCharacters = (value, minOtherChars) => {
  if ((value.match(/[^A-Z^a-z^0-9]/g) || []).length < minOtherChars) {
    return `Password must have at least ${minOtherChars} special ${pluralizeWord("letter", minOtherChars)}`;
  }
};

const checkForbiddenCharacters = (value, forbiddenChars) => {
  const forbiddenCharsRexExp = new RegExp(`[${forbiddenChars}]`);
  if (value.match(forbiddenCharsRexExp)) {
    return `The following characters should be avoided: ${forbiddenChars}`;
  }
};
